// components
import {
  Button,
  ClickAwayListener,
  IconButton,
  Typography
} from '@mui/material';
import {
  StyledBoxFlexAlignCenterSpaceBetween,
  StyledBoxFlexCenter,
  StyledBoxFlexCenterSmallGap,
  StyledBoxFlexCenterSpaceBetween
} from 'components/shared/Box/Box.styles';
import { IconContainer } from 'components/shared/Header/Header.styles';
import { Icon, IconButton as SdbIconButton } from '@sdb/planning-ui';
import {
  StyledMoreFiltersButton,
  StyledPopoverContainerBox,
  StyledPopoverContent,
  StyledPopoverFooter,
  StyledPopoverFooterActions,
  StyledPopoverHeader,
  StyledPopoverMainWrapper,
  StyledSettingsSection,
  StyledSettingsSectionContent
} from './FilterPopper.styles';

// hooks
import { useTranslation } from 'react-i18next';

// libraries
import { motion, useDragControls } from 'framer-motion';

// types
import type { FilterPopperProps } from './FilterPopper.types';

// utils
import { isClickOnTriggerOrLayer } from './FilterPopper.utils';
import {
  cloneElement,
  isValidElement,
  useCallback,
  useRef,
  useState
} from 'react';
import { StyledUnsavedDataAlertBoxRelative } from 'components/shared/CRUD/components/InnerPage/EditPage.styles';
import UnsavedDataAlert from 'components/shared/CRUD/components/InnerPage/components/UnsavedDataAlert';
import { StyledTypographyFlexGrow } from 'components/shared/Typography/Typography.styles';
import { FilterPopperWrapper } from './FilterPopperWrapper';
import { StyledFilterPopperPaper } from 'components/shared/Paper/StyledPaper.styles';

const MotionPaper = motion(StyledFilterPopperPaper);

export const FilterPopper = ({
  filterMainLabel,
  children,
  filterId,
  hasMoreFilters = true,
  handleClose,
  handleClearFilters,
  handleApplyFilters,
  enableSettings = false,
  settingsLabel,
  SettingsComponent,
  onSave,
  saveDisabled = false,
  isEditMode = false,
  settingsAction,
  isOpen,
  getAnchorEl,
  enablePopperPin = true,
  customZIndex,
  placement,
  onClickAway
}: FilterPopperProps) => {
  const open = isOpen;
  const filterPopoverId = open ? filterId : undefined;

  const { t } = useTranslation('common', {
    keyPrefix: 'flexMatch'
  });
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [pinned, setPinned] = useState(false);
  const paperRef = useRef<HTMLDivElement | null>(null);
  const [pinnedRect, setPinnedRect] = useState<{
    top: number;
    left: number;
  } | null>(null);

  const childWithProp = isValidElement(children)
    ? cloneElement(children as React.ReactElement<any>, {
        showMoreFilters,
        showSettings,
        isEditMode
      })
    : children;

  const handleShowSettings = () => {
    setShowSettings((prev) => !prev);
    showSettings && setShowMoreFilters(false);
    !showSettings && setShowMoreFilters(true);
  };

  const handleCloseOnClick = () => {
    if (pinned) {
      setPinned(false);
      setPinnedRect(null);
    }
    handleClose?.();
  };

  const handlePinPopover = () => {
    if (!pinned) {
      const el = paperRef.current;
      if (!el) return;

      const rect = el.getBoundingClientRect();

      setPinnedRect({ top: rect.top, left: rect.left });
      setPinned(true);
    } else {
      setPinned(false);
      setPinnedRect(null);
    }
  };

  // A pinned popper is parked on purpose, so a click elsewhere on the page leaves it be.
  const handleClickAway = (event: MouseEvent | TouchEvent) => {
    if (pinned || !onClickAway) return;
    if (isClickOnTriggerOrLayer(event.target, getAnchorEl())) return;
    onClickAway();
  };

  // Esc closes like ✕. A field that uses Esc itself — an Autocomplete closing its list, a menu — stops the
  // event or marks it handled, so its first Esc only closes the list and a second one the popper.
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== 'Escape' || event.defaultPrevented) return;
    handleCloseOnClick();
  };

  const dragControls = useDragControls();
  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      dragControls.start(e);
    },
    [dragControls]
  );

  const panel = (
    <StyledPopoverMainWrapper enableSettings={enableSettings}>
      {enableSettings && showSettings && SettingsComponent && (
        <StyledSettingsSection>
          <StyledSettingsSectionContent>
            <IconContainer iconSize="MEDIUM">
              <Icon type="gear" color="primary" />
            </IconContainer>
            <StyledTypographyFlexGrow variant="h6">
              {settingsLabel ? settingsLabel : t('settings')}
            </StyledTypographyFlexGrow>
            {settingsAction &&
              settingsAction.onClick &&
              settingsAction.label &&
              settingsAction.icon && (
                <SdbIconButton
                  onClick={settingsAction.onClick}
                  type={settingsAction.icon}
                  title={settingsAction.label}
                />
              )}
          </StyledSettingsSectionContent>
          {SettingsComponent}
        </StyledSettingsSection>
      )}
      <StyledPopoverContainerBox>
        <StyledPopoverHeader>
          <StyledBoxFlexAlignCenterSpaceBetween>
            <StyledBoxFlexCenter>
              <IconContainer iconSize="MEDIUM">
                <Icon type="sliders" color="primary" />
              </IconContainer>
              <Typography variant="h6">
                {filterMainLabel ?? t('filter')}
              </Typography>
            </StyledBoxFlexCenter>

            <StyledBoxFlexCenterSmallGap>
              {isEditMode && showSettings && (
                <StyledUnsavedDataAlertBoxRelative>
                  <UnsavedDataAlert textContent={t('editModeActive')} />
                </StyledUnsavedDataAlertBoxRelative>
              )}
              {enableSettings && (
                <IconButton onClick={handleShowSettings}>
                  <IconContainer iconSize="MEDIUM">
                    <Icon type="gear" />
                  </IconContainer>
                </IconButton>
              )}

              {enablePopperPin && (
                <>
                  {pinned && (
                    <IconButton
                      onPointerDown={handlePointerDown}
                      style={{ cursor: 'move', userSelect: 'none' }}
                      title="Drag"
                    >
                      <IconContainer iconSize="MEDIUM">
                        <Icon type="grip-dots-vertical" />
                      </IconContainer>
                    </IconButton>
                  )}

                  <IconButton onClick={handlePinPopover} title="Unpin">
                    <IconContainer iconSize="MEDIUM">
                      <Icon type={pinned ? 'thumbtack-fill' : 'thumbtack'} />
                    </IconContainer>
                  </IconButton>
                </>
              )}

              <IconButton onClick={handleCloseOnClick}>
                <IconContainer iconSize="MEDIUM">
                  <Icon type="xmark" />
                </IconContainer>
              </IconButton>
            </StyledBoxFlexCenterSmallGap>
          </StyledBoxFlexAlignCenterSpaceBetween>
        </StyledPopoverHeader>

        <StyledPopoverContent>{childWithProp}</StyledPopoverContent>
        <StyledPopoverFooter>
          <StyledBoxFlexCenterSpaceBetween>
            {hasMoreFilters && (
              <StyledMoreFiltersButton
                variant="text"
                onClick={() => setShowMoreFilters((v) => !v)}
              >
                <IconContainer iconSize="MEDIUM">
                  <Icon type={showMoreFilters ? 'minus' : 'plus'} />
                </IconContainer>
                {showMoreFilters ? t('showLessFilters') : t('showMoreFilters')}
              </StyledMoreFiltersButton>
            )}
            <StyledPopoverFooterActions>
              {isEditMode && showSettings ? (
                <Button onClick={onSave} disabled={saveDisabled}>
                  {t('save', { keyPrefix: '' })}
                </Button>
              ) : (
                <>
                  <Button variant="outlined" onClick={handleClearFilters}>
                    {t('clearFilters')}
                  </Button>
                  <Button onClick={handleApplyFilters}>
                    {t('applyFilters')}
                  </Button>
                </>
              )}
            </StyledPopoverFooterActions>
          </StyledBoxFlexCenterSpaceBetween>
        </StyledPopoverFooter>
      </StyledPopoverContainerBox>
    </StyledPopoverMainWrapper>
  );

  return (
    <FilterPopperWrapper
      pinned={pinned}
      filterPopoverId={filterPopoverId}
      getAnchorEl={getAnchorEl}
      pinnedRect={pinnedRect}
      customZIndex={customZIndex}
      placement={placement}
    >
      <MotionPaper
        ref={paperRef}
        elevation={8}
        drag={pinned}
        dragControls={dragControls}
        dragListener={false}
        dragElastic={0}
        dragMomentum={false}
        id="filter-popper-motion-paper"
        onKeyDown={onClickAway ? handleKeyDown : undefined}
      >
        {onClickAway ? (
          <ClickAwayListener onClickAway={handleClickAway}>
            {panel}
          </ClickAwayListener>
        ) : (
          panel
        )}
      </MotionPaper>
    </FilterPopperWrapper>
  );
};
